import type { ProductoCatalogo } from '@/modules/cotizador/useCotizador'

/**
 * EL CATÁLOGO, GUARDADO EN EL TELÉFONO.
 *
 * En muchas obras no hay señal, y hasta hoy eso dejaba al técnico sin poder
 * cotizar: cada letra que escribía en el buscador iba al servidor. Johan lo
 * dijo con la frontera exacta: «no necesitamos stock real, ese puede ser con
 * señal» — los materiales y sus precios SÍ pueden viajar descargados.
 *
 * Por eso esto guarda el catálogo entero —unos 2.700 productos, que ocupan
 * poco más de medio mega— en IndexedDB, el almacenamiento privado de la
 * aplicación, y lo busca ahí cuando no hay red.
 *
 * LO QUE NO SE GUARDA ES EL STOCK, y no por olvido: un «hay 14 en bodega»
 * descargado el martes es mentira el jueves, y es peor que no decir nada
 * porque se usa para decidir si hay que comprar. Sin señal el cotizador
 * trabaja sin esa columna y lo avisa.
 *
 * Capa mínima sobre IndexedDB, sin librería, como la cola de fotos: guardar,
 * buscar, contar y saber de cuándo es.
 */

const BASE = 'mega-catalogo'
const ALMACEN = 'productos'
const CLAVES = 'meta'
const VERSION = 1

let promesaBase: Promise<IDBDatabase> | null = null

function abrir(): Promise<IDBDatabase> {
  if (promesaBase) return promesaBase

  promesaBase = new Promise((resolver, rechazar) => {
    const solicitud = indexedDB.open(BASE, VERSION)

    solicitud.onupgradeneeded = () => {
      const db = solicitud.result
      if (!db.objectStoreNames.contains(ALMACEN)) {
        db.createObjectStore(ALMACEN, { keyPath: 'id_inventario' })
      }
      if (!db.objectStoreNames.contains(CLAVES)) {
        db.createObjectStore(CLAVES)
      }
    }

    solicitud.onsuccess = () => resolver(solicitud.result)
    solicitud.onerror = () => rechazar(solicitud.error)
  })

  return promesaBase
}

/** Guarda el catálogo completo, reemplazando el anterior. */
export async function guardarCatalogo(productos: ProductoCatalogo[]): Promise<void> {
  const db = await abrir()

  await new Promise<void>((resolver, rechazar) => {
    const t = db.transaction([ALMACEN, CLAVES], 'readwrite')
    const almacen = t.objectStore(ALMACEN)

    // Se vacía antes: un producto que dejó de existir en World Office no debe
    // quedar vivo aquí para siempre.
    almacen.clear()
    for (const p of productos) {
      // El stock no viaja: sin señal no hay forma de saber si sigue siendo
      // cierto, y un número viejo se usaría como si lo fuera.
      almacen.put({ ...p, stock: 0 })
    }
    t.objectStore(CLAVES).put(new Date().toISOString(), 'actualizado')

    t.oncomplete = () => resolver()
    t.onerror = () => rechazar(t.error)
  })
}

/** Cuándo se descargó por última vez. Null si nunca. */
export async function cuandoSeDescargo(): Promise<string | null> {
  const db = await abrir()

  return new Promise((resolver) => {
    const t = db.transaction(CLAVES, 'readonly')
    const s = t.objectStore(CLAVES).get('actualizado')
    s.onsuccess = () => resolver((s.result as string | undefined) ?? null)
    s.onerror = () => resolver(null)
  })
}

export async function cuantosGuardados(): Promise<number> {
  const db = await abrir()

  return new Promise((resolver) => {
    const t = db.transaction(ALMACEN, 'readonly')
    const s = t.objectStore(ALMACEN).count()
    s.onsuccess = () => resolver(s.result)
    s.onerror = () => resolver(0)
  })
}

/**
 * Busca en lo guardado.
 *
 * Mismo criterio que el servidor —texto contenido en la descripción— para que
 * el técnico no vea una lista distinta según tenga señal o no. Se normaliza
 * sin tildes porque en la obra nadie las escribe.
 */
export async function buscarEnCatalogo(
  termino: string,
  limite = 25,
): Promise<ProductoCatalogo[]> {
  const buscado = normalizar(termino)
  if (buscado.length < 2) return []

  const db = await abrir()

  return new Promise((resolver) => {
    const encontrados: ProductoCatalogo[] = []
    const t = db.transaction(ALMACEN, 'readonly')
    const cursor = t.objectStore(ALMACEN).openCursor()

    cursor.onsuccess = () => {
      const c = cursor.result
      if (!c || encontrados.length >= limite) {
        encontrados.sort((a, b) => a.descripcion.localeCompare(b.descripcion))
        resolver(encontrados)
        return
      }

      const p = c.value as ProductoCatalogo
      if (normalizar(p.descripcion).includes(buscado)) encontrados.push(p)
      c.continue()
    }

    cursor.onerror = () => resolver([])
  })
}

/** Sin tildes y en minúsculas: en la obra nadie escribe «aislación». */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
}


/* ═══════════════════════════════════════════════════════════════════════════
   Y LOS CLIENTES, por lo mismo.

   Levantar una obra en la calle exige escoger el cliente, y esa lista vive en
   el servidor: sin señal el formulario se planta en «Falta el cliente» y la
   visita se pierde. Son unos 2.700 terceros y ocupan parecido al catálogo de
   materiales.

   Lo que esto NO resuelve: un cliente que no existe todavía. Crearlo exige
   World Office, y eso no se puede fingir desde un teléfono sin cobertura.
   ═══════════════════════════════════════════════════════════════════════════ */

const BASE_CLIENTES = 'mega-clientes'
const ALMACEN_CLIENTES = 'terceros'

let promesaClientes: Promise<IDBDatabase> | null = null

function abrirClientes(): Promise<IDBDatabase> {
  if (promesaClientes) return promesaClientes

  promesaClientes = new Promise<IDBDatabase>((resolver, rechazar) => {
    const s = indexedDB.open(BASE_CLIENTES, 1)
    s.onupgradeneeded = () => {
      const db = s.result
      if (!db.objectStoreNames.contains(ALMACEN_CLIENTES)) {
        db.createObjectStore(ALMACEN_CLIENTES, { keyPath: 'id_tercero' })
      }
    }
    s.onsuccess = () => resolver(s.result)
    s.onerror = () => rechazar(s.error)
    s.onblocked = () => rechazar(new Error('El almacenamiento está ocupado.'))
  })

  promesaClientes.catch(() => { promesaClientes = null })
  return promesaClientes
}

export async function guardarClientes(clientes: Record<string, unknown>[]): Promise<void> {
  const db = await abrirClientes()

  await new Promise<void>((resolver, rechazar) => {
    const t = db.transaction(ALMACEN_CLIENTES, 'readwrite')
    const almacen = t.objectStore(ALMACEN_CLIENTES)
    almacen.clear()
    for (const c of clientes) almacen.put(c)
    t.oncomplete = () => resolver()
    t.onerror = () => rechazar(t.error)
  })
}

export async function cuantosClientesGuardados(): Promise<number> {
  const db = await abrirClientes()
  return new Promise((r) => {
    const t = db.transaction(ALMACEN_CLIENTES, 'readonly')
    const s = t.objectStore(ALMACEN_CLIENTES).count()
    s.onsuccess = () => r(s.result)
    s.onerror = () => r(0)
  })
}

/** Busca por nombre o documento, con las mismas reglas que el servidor. */
export async function buscarClientesGuardados(
  termino: string,
  limite = 24,
): Promise<Record<string, unknown>[]> {
  const palabras = normalizar(termino).split(/\s+/).filter(Boolean)
  const db = await abrirClientes()

  return new Promise((resolver) => {
    const encontrados: Record<string, unknown>[] = []
    const t = db.transaction(ALMACEN_CLIENTES, 'readonly')
    const cursor = t.objectStore(ALMACEN_CLIENTES).openCursor()

    cursor.onsuccess = () => {
      const c = cursor.result
      if (!c || encontrados.length >= limite) { resolver(encontrados); return }

      const fila = c.value as Record<string, unknown>
      const texto = normalizar(
        `${fila.nombre ?? ''} ${fila.identificacion ?? ''} ${fila.documento ?? ''}`,
      )
      // Todas las palabras, en cualquier orden: igual que el servidor.
      if (palabras.length === 0 || palabras.every((p) => texto.includes(p))) {
        encontrados.push(fila)
      }
      c.continue()
    }

    cursor.onerror = () => resolver([])
  })
}
